import type { EnterInfo } from './party';
import { type JsonMessage, type Room, type Transport, asText, randomId } from './transport';

/**
 * Matchmaking (research 04 §5.3): each queueable activity has a public lobby
 * room. Queued players announce themselves; the longest-waiting player acts
 * as organiser and, once the group is full (or at least `min` players waited
 * `FILL_WAIT_MS`), picks the group, hosts the instance and tells the others
 * where to go. PvP groups are split into two teams by item level.
 */

const HEARTBEAT_MS = 2000;
const STALE_MS = 7000;
export const FILL_WAIT_MS = 30000;

export interface QueueSpec {
  content: string;
  label: string;
  size: number;
  /** Smallest group that may start after waiting `FILL_WAIT_MS`. */
  min: number;
  pvp?: boolean;
  opts: Record<string, string | number | boolean>;
}

export interface QueueMember {
  uid: string;
  name: string;
  classId: string;
  level: number;
  ilvl: number;
}

interface Entry extends QueueMember {
  peer: string;
  at: number;
  seen: number;
}

export interface QueueDeps {
  transport: Transport;
  self(): QueueMember;
  now(): number;
  changed(): void;
  enter(info: EnterInfo, team: 'red' | 'blue' | null): void;
  notify(text: string): void;
}

export class MatchMaker {
  spec: QueueSpec | null = null;
  room: Room | null = null;
  since = 0;
  readonly entries = new Map<string, Entry>();
  private offs: (() => void)[] = [];
  private lastBeat = 0;

  constructor(private d: QueueDeps) {}

  get queued(): boolean {
    return this.spec !== null;
  }

  /** Players in the queue including us. */
  count(): number {
    return this.spec ? 1 + this.entries.size : 0;
  }

  async join(spec: QueueSpec): Promise<void> {
    this.cancel();
    this.spec = spec;
    this.since = this.d.now();
    this.entries.clear();
    this.d.changed();
    // Different difficulties never share a queue.
    const variant = String(spec.opts.difficulty ?? (spec.opts.hard === true ? 'hard' : 'normal'));
    const room = await this.d.transport.join(`mm:${spec.content}:${spec.size}:${variant}`);
    if (this.spec !== spec) {
      void room.leave();
      return;
    }
    this.room = room;
    this.offs.push(
      room.onMessage((from, m) => this.onMessage(from, m)),
      room.onPeerJoin((peer) => this.announce(peer)),
      room.onPeerLeave((peer) => {
        if (this.entries.delete(peer)) this.d.changed();
      }),
    );
    this.announce();
  }

  cancel(): void {
    if (!this.spec) return;
    this.room?.send({ t: 'unq' });
    for (const off of this.offs) off();
    this.offs = [];
    void this.room?.leave();
    this.room = null;
    this.spec = null;
    this.entries.clear();
    this.d.changed();
  }

  private announce(to?: string): void {
    this.lastBeat = this.d.now();
    this.room?.send({ t: 'q', m: this.d.self(), at: this.since }, to);
  }

  private onMessage(from: string, m: JsonMessage): void {
    const spec = this.spec;
    if (!spec) return;
    const now = this.d.now();
    switch (m.t) {
      case 'q': {
        const mm = m.m as Partial<QueueMember> | undefined;
        if (!mm || typeof mm.uid !== 'string') return;
        const isNew = !this.entries.has(from);
        this.entries.set(from, { peer: from, uid: mm.uid, name: asText(mm.name, '?', 24), classId: asText(mm.classId), level: Number(mm.level) || 1, ilvl: Number(mm.ilvl) || 0, at: Number(m.at) || now, seen: now });
        if (isNew) this.d.changed();
        return;
      }
      case 'unq':
        if (this.entries.delete(from)) this.d.changed();
        return;
      case 'mgo': {
        const members = Array.isArray(m.members) ? m.members : [];
        const info = m.info as EnterInfo | undefined;
        const self = this.d.transport.selfId;
        if (!info || !members.includes(self) || typeof info.zone !== 'string' || typeof info.inst !== 'string') {
          // Someone else's group formed: forget them.
          for (const p of members) if (typeof p === 'string') this.entries.delete(p);
          this.d.changed();
          return;
        }
        const teams = (m.teams ?? {}) as Record<string, 'red' | 'blue'>;
        const team = teams[self] === 'red' || teams[self] === 'blue' ? teams[self] : null;
        this.cancel();
        this.d.enter({ zone: info.zone, inst: info.inst, pw: String(info.pw ?? ''), host: from, size: Number(info.size) || members.length, opts: info.opts && typeof info.opts === 'object' ? info.opts : {} }, team);
        return;
      }
      default:
        return;
    }
  }

  /** Everyone queued (us included), longest waiting first. */
  private lineup(): Entry[] {
    const me = this.d.self();
    const self: Entry = { ...me, peer: this.d.transport.selfId, at: this.since, seen: this.d.now() };
    return [self, ...this.entries.values()].sort((a, b) => a.at - b.at || (a.peer < b.peer ? -1 : 1));
  }

  tick(): void {
    const spec = this.spec;
    if (!spec || !this.room) return;
    const now = this.d.now();
    if (now - this.lastBeat >= HEARTBEAT_MS) this.announce();
    let pruned = false;
    for (const [peer, e] of this.entries) {
      if (now - e.seen > STALE_MS) {
        this.entries.delete(peer);
        pruned = true;
      }
    }
    if (pruned) this.d.changed();
    const line = this.lineup();
    const organiser = line[0];
    if (!organiser || organiser.peer !== this.d.transport.selfId) return;
    const full = line.length >= spec.size;
    const longEnough = line.length >= spec.min && now - this.since >= FILL_WAIT_MS;
    if (!full && !longEnough) return;
    this.start(line.slice(0, spec.size));
  }

  private start(group: Entry[]): void {
    const spec = this.spec;
    if (!spec) return;
    const self = this.d.transport.selfId;
    const teams: Record<string, 'red' | 'blue'> = {};
    if (spec.pvp) {
      // Snake draft by item level keeps the teams even.
      const byIlvl = [...group].sort((a, b) => b.ilvl - a.ilvl);
      byIlvl.forEach((e, i) => (teams[e.peer] = i % 4 === 0 || i % 4 === 3 ? 'red' : 'blue'));
    }
    const info: EnterInfo = { zone: spec.content, inst: randomId(12), pw: randomId(12), host: self, size: group.length, opts: spec.opts };
    this.room?.send({ t: 'mgo', info, members: group.map((e) => e.peer), teams });
    this.cancel();
    this.d.enter(info, spec.pvp ? (teams[self] ?? 'red') : null);
  }
}
